import * as React from "react";
import { Grid, makeStyles } from "@material-ui/core";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import TwitterIcon from "@material-ui/icons/Twitter";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import YouTubeIcon from "@material-ui/icons/YouTube";
import { AfriqContainer } from "./AfriqContainer";
import { useTranslation } from "react-i18next";
import { ELinks } from "../../../utils/enum";
import * as footerService from "../../../Services/footerServices";
import { setRef } from "../../../utils/footerUtils";
//import { useHistory } from "react-router-dom";
import { ELinksURL } from "../../../utils/enum/linksOnFooter";
import TikTokIcon from "../TikTokIcon";
import { FooterContext } from "../../../contexts/AppContext";

const useStyles = makeStyles((theme) => ({
    footer_container: {
        width: "100%",
        borderTop: "1px solid #DDDDDD",
        background: "#2B2B2B",
        position: "absolute",
        zIndex: 10,
    },
    gridContainer: {
        [theme.breakpoints.up("md")]: {
            "& > div:not(:first-child)": {
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: -54,
                    display: "block",
                    width: 1,
                    height: 127,
                    background: "#FFFFFF",
                },
            },
        },
    },
    gridItem: {
        position: "relative",
        marginTop: 30,
        marginBottom: 30,
        [theme.breakpoints.down("sm")]: {
            alignItems: "center",
        },
        [theme.breakpoints.up("md")]: {
            flex: "1 1 0px", // This makes items grow equally to fill space
            maxWidth: "25%", // 4 items per row (100%/4)
        },
    },
    link: {
        display: "flex",
        alignItems: "center",
        fontSize: 14,
        fontWeight: 400,
        margin: 0,
        cursor: "pointer",
        color: "#FFFFFF",
        "&:hover": {
            textDecoration: "underline",
        },
        "&:not(:last-child)": {
            marginBottom: 20,
        },
    },
    skeletonItem: {
        background: '#3D3D3D',
        borderRadius: 4,
        width: '60%'
    },
    footerPlaceholder: {
        background: '#3D3D3D',
        borderRadius: 4,
        width: '60%'
    },
    icon: {
        marginRight: 18,
        fill: "currentColor",
        width: 24,
        height: 24,
    },
}));

const Footer = () => {
    const classes = useStyles();
    const { t } = useTranslation<'common'>();

    // Use FooterContext instead of local state
    const { footerData } = React.useContext(FooterContext);
    const { links, isLoading } = footerData;
    
    if (isLoading) {
        return (
            <div className={classes.footerPlaceholder}>
                <AfriqContainer>
                    <div className={classes.skeletonItem} />
                    <div className={classes.skeletonItem} />
                    <div className={classes.skeletonItem} />
                </AfriqContainer>
            </div>
        );
    }

    return (
        <div className={classes.footer_container}>
            <AfriqContainer>
                <Grid
                    container
                    className={classes.gridContainer}
                    justifyContent="center"
                    spacing={0}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        container
                        direction="column"
                        className={classes.gridItem}
                    >
                        <a
                            href={ELinksURL.FAQ}
                            className={classes.link}
                            title={t("titleFooter.faq")}
                        >
                            FAQ
                        </a>
                        <a
                            href={ELinksURL.A_PROPOS_DE_NOUS}
                            className={classes.link}
                            title={t("titleFooter.about_us")}
                        >
                            {t("label.a-propos-de-nous")}
                        </a>
                        <a
                            href={ELinksURL.NOS_VALEURS}
                            className={classes.link}
                            title={t("titleFooter.our_values")}
                        >
                            {t("label.nos-valeurs")}
                        </a>
                        <a
                            href={ELinksURL.NOUS_CONTACTER}
                            className={classes.link}
                            title={t("titleFooter.contact_us")}
                        >
                            {t("label.nous-contacter")}
                        </a>
                        <a 
                            href="/siteplan" 
                            className={classes.link}
                        >
                            {t("label.site-plan")}
                        </a>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        container
                        direction="column"
                        className={classes.gridItem}
                    >
                        <a
                            href={ELinksURL.GARANTIE_DE_PROTECTION_DES_VOYAGEURS}
                            className={classes.link}
                            title={t("titleFooter.traveler_protection_guarantee")}
                        >
                            {t("label.garantie-de-protection-des-voyageurs")}
                        </a>
                        <a
                            href={ELinksURL.CGU}
                            className={classes.link}
                            title={t("titleFooter.terms_and_conditions")}
                        >
                            {t("label.gcu")}
                        </a>
                        <a
                            href={ELinksURL.PDC}
                            className={classes.link}
                            title={t("titleFooter.privacy_policy")}
                        >
                            {t("label.dpp")}
                        </a>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        container
                        direction="column"
                        className={classes.gridItem}
                    >
                        <a
                            href={links[setRef(ELinks.FACEBOOK).key]?.state ?? "#"}
                            target="_blank"
                            className={classes.link}
                            rel="noopener noreferrer"
                            title={t("titleFooter.follow_us_on_facebook")}
                        >
                            <FacebookIcon className={classes.icon} aria-label="Facebook" />
                            {setRef(ELinks.FACEBOOK).name}
                        </a>
                        <a
                            href={links[setRef(ELinks.INSTAGRAM).key]?.state ?? "#"}
                            target="_blank"
                            className={classes.link}
                            rel="noopener noreferrer"
                            title={t("titleFooter.follow_us_on_instagram")}
                        >
                            <InstagramIcon className={classes.icon} aria-label="Instagram" />
                            {setRef(ELinks.INSTAGRAM).name}
                        </a>
                        <a
                            href={links[setRef(ELinks.TWITTER).key]?.state ?? "#"}
                            target="_blank"
                            className={classes.link}
                            rel="noopener noreferrer"
                            title={t("titleFooter.follow_us_on_twitter")}
                        >
                            <TwitterIcon className={classes.icon} aria-label="Twitter" />
                            {setRef(ELinks.TWITTER).name}
                        </a>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        container
                        direction="column"
                        className={classes.gridItem}
                    >
                        <a
                            href={links[setRef(ELinks.LINKEDIN).key]?.state ?? "#"}
                            target="_blank"
                            className={classes.link}
                            rel="noopener noreferrer"
                            title={t("titleFooter.follow_us_on_linkedin")}
                        >
                            <LinkedInIcon className={classes.icon} aria-label="LinkedIn" />
                            {setRef(ELinks.LINKEDIN).name}
                        </a>
                        <a
                            href={links[setRef(ELinks.TIKTOK).key]?.state ?? "#"}
                            target="_blank"
                            className={classes.link}
                            rel="noopener noreferrer"
                            title={t("titleFooter.follow_us_on_tiktok")}
                        >
                            <TikTokIcon className={classes.icon} aria-label="TikTok" />
                            {setRef(ELinks.TIKTOK).name}
                        </a>
                        <a
                            href={links[setRef(ELinks.YOUTUBE).key]?.state ?? "#"}
                            target="_blank"
                            className={classes.link}
                            rel="noopener noreferrer"
                            title={t("titleFooter.follow_us_on_youtube")}
                        >
                            <YouTubeIcon className={classes.icon} aria-label="YouTube" />
                            {setRef(ELinks.YOUTUBE).name}
                        </a>
                    </Grid>
                </Grid>
            </AfriqContainer>
        </div>
    );
}

export default Footer;